<template>
  <ListItem
    :class="{ 'opacity-50': car.status === CarStatus.FLOAT_OUT }"
    :drop-down-items="dropDownItems"
    :to="
      !onClickFunction
        ? {
            name: $can('ros', 'VehiclePool') ? 'editCar' : 'viewCar',
            params: { id: props.car.id },
          }
        : undefined
    "
    @click="onClickFunction"
  >
    <figure class="basis-1/4 self-center md:basis-[14%]">
      <img :src="imageUrl" />
    </figure>

    <div class="basis-3/4 items-center md:flex">
      <div class="flex basis-[60%] flex-col md:ml-6">
        <p class="grid items-center font-semibold md:flex md:gap-2">
          <span
            ><LicencePlate v-if="!car?.isGhostCar">{{
              car?.licencePlate || 'X-XX XXXX'
            }}</LicencePlate></span
          >
          <span>{{ vehicleType?.make.name }} {{ vehicleType?.model }}</span>
        </p>
        <p class="flex items-center gap-1">
          <MapPinIcon class="size-4 text-obsidian-900 opacity-30" />{{
            getLocalizedField(currentStation.info)
          }}
        </p>
      </div>

      <div class="flex basis-[40%] flex-col">
        <p class="flex items-center gap-1">
          <HashIcon class="size-4 text-obsidian-900 opacity-30" />{{ car.fleetNumber }}
        </p>
        <p v-if="!car?.isGhostCar" class="flex items-center gap-1">
          <ScanBarcodeIcon class="size-4 text-obsidian-900 opacity-30" />{{ car.fin || '-' }}
        </p>
        <p class="flex items-center gap-1">
          <PaintbrushVerticalIcon class="size-4 text-obsidian-900 opacity-30" />{{
            car.color ? t(`carColors.${car.color}`) : '-'
          }}
        </p>
      </div>
    </div>

    <template v-if="workshopAppointment" #suffix>
      <ListItemAnnotation>
        <WorkshopAppointmentListItem :appointment="workshopAppointment" :clickable="false" />
      </ListItemAnnotation>
    </template>
  </ListItem>
</template>

<script lang="ts" setup>
import { type DropdownItem } from '@/components/DropdownDotted.vue';
import ListItemAnnotation from '@/components/ListItemAnnotation.vue';
import type { Car } from '@/entities/cars/car.entity';
import type { VehicleType } from '@/entities/vehicle-type.entity';
import type { WorkshopAppointment } from '@/entities/workshop-appointments/workshop-appointment.entity';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { computed, toRef } from 'vue';
import { useCurrentStationOfCar } from '@/hooks/use-current-station-of-car';
import ListItem from '@/components/ListItem.vue';
import LicencePlate from '@/components/LicencePlate.vue';
import { useLocalizedFields } from '@/hooks/use-localized-fields.ts';
import { HashIcon, MapPinIcon, PaintbrushVerticalIcon, ScanBarcodeIcon } from 'lucide-vue-next';
import { CarStatus } from '@/entities/cars/car-status.enum.ts';
import WorkshopAppointmentListItem from '@/views/vehicle-pool/components/WorkshopAppointmentListItem.vue';

const props = defineProps<{
  car: Car;
  vehicleType?: VehicleType;
  workshopAppointment?: WorkshopAppointment;
  dropDownItems?: DropdownItem[];
  onClickFunction?: () => void;
}>();
const { t } = useI18n();
const { getLocalizedField } = useLocalizedFields();

const router = useRouter();

const imageUrl = computed(() => props.car.imageUrl ?? props.vehicleType?.imageUrl);

const currentStation = useCurrentStationOfCar(toRef(props, 'car'));

const dropDownItems = computed<DropdownItem[]>(
  () =>
    props.dropDownItems ?? [
      {
        title: t('addMaintenanceBlock'),
        onClick: () =>
          router.push({
            name: 'maintenanceEdit',
            params: { carId: props.car.id, blockId: 'new' },
          }),
      },
      {
        title: t('addPartnerRent'),
        onClick: () =>
          router.push({
            name: 'partnerRentEdit',
            params: { carId: props.car.id, allocationId: 'new' },
          }),
      },
    ],
);
</script>

<i18n lang="json">
{
  "en": {
    "numberOfCars": "Number of cars",
    "addMaintenanceBlock": "+ Add Maintenance Block",
    "addPartnerRent": "+ Add Partner Rent"
  },
  "de": {
    "numberOfCars": "Anzahl Autos",
    "addMaintenanceBlock": "+ Wartungsblock hinzufügen",
    "addPartnerRent": "+ Partnermiete hinzufügen"
  },
  "es": {
    "numberOfCars": "Número de vehículos",
    "addMaintenanceBlock": "+ Añadir bloque de mantenimiento",
    "addPartnerRent": "+ Añadir alquiler partner"
  }
}
</i18n>
